"use client";
import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";

const ProductTypeModal = ({ isOpen, onClose, onSave, productType }) => {
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    status: "Active",
  });

useEffect(() => {
  if (productType) {
    const statusValue =
      productType.status &&
      productType.status.toString().trim().toLowerCase() === "inactive"
        ? "Inactive"
        : "Active";

    setFormData({
      name: productType.type_name || "",
      status: statusValue,
    });
  } else {
    setFormData({
      name: "",
      status: "Active",
    });
  }
}, [productType, isOpen]);

  // Escape key close + body scroll lock
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

const handleChange = (e) => {
  let value = e.target.value;

  // Capitalize first letter
  if (e.target.name === "name") {
    value = value.charAt(0).toUpperCase() + value.slice(1);
  }

  setFormData({
    ...formData,
    [e.target.name]: value,
  });
};

// ProductTypeModal.jsx
const handleSubmit = () => {
  if (!formData.name.trim()) return;
  onSave({
    type_name: formData.name,
    status: formData.status,
  });
  onClose();
};

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />

      {/* Panel */}
      <div
        ref={modalRef}
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col overflow-hidden transform transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white tracking-tight">
            {productType ? "Edit Product Type" : "Add Product Type"}
          </h2>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">
              Product Type Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Customized Product"
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm transition-colors"
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
              {["Active", "Inactive"].map(s => (
                <button key={s} type="button"
                  onClick={() => setFormData(f => ({ ...f, status: s }))}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    formData.status === s
                      ? s === "Active" ? "bg-white text-green-700 shadow-sm ring-1 ring-green-200" : "bg-white text-red-600 shadow-sm ring-1 ring-red-200"
                      : "text-gray-500 hover:text-gray-700"
                  }`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-100">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} disabled={!formData.name.trim()}
              className="flex-1 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-40 transition-colors shadow-sm">
              {productType ? "Update Type" : "Add Type"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductTypeModal;